import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import * as api from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { isStaffGuardCancelledError } from '@/libs/errors'
import { BulkOperationPeriodType } from '@/models/room-manager/bulk-operation'
import { BulkOperationModal } from './bulk-operation-modal'
import { BulkOperationNote } from './bulk-operation-field'
import { BulkOperationPeriodField } from './bulk-operation-period-field'

type SiteControllerStockBulkUpdateModalProps = {
  onCancel: () => void
  onCompleted: () => void
}

export type StockBulkUpdateFormValue = {
  period: BulkOperationPeriodType
}

export const SiteControllerStockBulkUpdateModal: React.FC<SiteControllerStockBulkUpdateModalProps> = ({ onCancel, onCompleted }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<StockBulkUpdateFormValue>({
    mode: 'all',
    defaultValues: {
      period: {
        beginDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().add(1, 'month').subtract(1, 'day').format('YYYY-MM-DD'),
      },
    },
  })

  const onSubmit = async ({ period }: StockBulkUpdateFormValue) => {
    try {
      setIsSubmitting(true)
      await api.bulkUpdateSiteControllerStock(period)
      window.alert(t('roomManagerBulk.StockUpdateSucceeded'))
    } catch (e) {
      // スタッフ確認でキャンセルした場合は更新していないため、モーダルを開いたままにする
      if (isStaffGuardCancelledError(e)) return
      errorHandler(e)
    } finally {
      setIsSubmitting(false)
    }
    // 失敗時も一部の期間・部屋タイプは更新されているため、成否に関わらずアサイン画面を再描画する。
    // 成否のアラートはブロッキングのため、閉じた後に再描画される
    onCompleted()
  }

  return (
    <BulkOperationModal
      title={t('roomManagerBulk.StockUpdateTitle')}
      description={t('roomManagerBulk.StockUpdateDescription')}
      submitLabel={t('roomManagerBulk.StockUpdateSubmit')}
      isSubmittable={isValid}
      isSubmitting={isSubmitting}
      width={560}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
    >
      <BulkOperationPeriodField control={control} disabled={isSubmitting} />
      <BulkOperationNote items={[t('roomManagerBulk.StockUpdateNoteOverwrite'), t('roomManagerBulk.StockUpdateNoteDelay')]} />
    </BulkOperationModal>
  )
}
